import React, { useState, useEffect } from 'react';
import { 
  HomepageProductSection, 
  HeroSectionData, 
  PromotionalBannerData,
  ProductCollection,
  createEmptyHomepageProductSection,
  createEmptyHeroSectionData,
  createEmptyPromotionalBannerData
} from '../../types';
import { storefrontService } from '../../services/storefrontService';
import { getCollections } from '../../services/firebase';
import Button from '../../components/atoms/Button';
import Icon from '../../components/atoms/Icon';
import Input from '../../components/atoms/Input';
import Toast from '../../components/atoms/Toast';
import { HomepageImageUploader } from '../../components/admin/molecules';
import './StorefrontPage.css';

interface StorefrontPageState {
  homepageSections: HomepageProductSection[];
  reorderedSections: HomepageProductSection[] | null;
  isReordering: boolean;
  draggingSectionIndex: number | null;
  collections: ProductCollection[];
  heroSection: HeroSectionData | null;
  promotionalBanner: PromotionalBannerData | null;
  loading: boolean;
  error: string | null;
  activeTab: 'sections' | 'hero' | 'banner';
  editingSection: HomepageProductSection | null;
  editingHero: boolean;
  editingBanner: boolean;
  showToast: boolean;
  toastMessage: string;
  toastType: 'success' | 'error';
}

const StorefrontPage: React.FC = () => {
  const [state, setState] = useState<StorefrontPageState>({
    homepageSections: [],
    reorderedSections: null,
    isReordering: false,
    draggingSectionIndex: null,
    collections: [],
    heroSection: null,
    promotionalBanner: null,
    loading: true,
    error: null,
    activeTab: 'sections',
    editingSection: null,
    editingHero: false,
    editingBanner: false,
    showToast: false,
    toastMessage: '',
    toastType: 'success'
  });

  // Form states
  const [sectionForm, setSectionForm] = useState<Partial<HomepageProductSection>>(createEmptyHomepageProductSection());
  const [heroForm, setHeroForm] = useState<Partial<HeroSectionData>>(createEmptyHeroSectionData());
  const [bannerForm, setBannerForm] = useState<Partial<PromotionalBannerData>>(createEmptyPromotionalBannerData());

  useEffect(() => {
    loadStorefrontData();
  }, []);

  const loadStorefrontData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const [sections, collections, hero, banner] = await Promise.all([
        storefrontService.getHomepageProductSections(),
        getCollections(),
        storefrontService.getHeroSectionData(),
        storefrontService.getPromotionalBannerData()
      ]);

      setState(prev => ({
        ...prev,
        homepageSections: sections,
        collections,
        heroSection: hero,
        promotionalBanner: banner,
        loading: false
      }));

      // Initialize forms with current data
      if (hero) {
        setHeroForm(hero);
      }
      if (banner) {
        setBannerForm({
          ...banner,
          note: banner.note || '' // Ensure note field exists
        });
      }
    } catch (error) {
      console.error('Error loading storefront data:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load storefront data'
      }));
    }
  };

  const handleInitializeData = async () => {
    if (window.confirm('This will initialize the storefront with default data. Continue?')) {
      try {
        setState(prev => ({ ...prev, loading: true }));
        await storefrontService.initializeDefaultData();
        showToast('Default data initialized successfully');
        await loadStorefrontData();
      } catch (error) {
        console.error('Error initializing default data:', error);
        showToast('Failed to initialize default data', 'error');
        setState(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setState(prev => ({
      ...prev,
      showToast: true,
      toastMessage: message,
      toastType: type
    }));
    
    setTimeout(() => {
      setState(prev => ({ ...prev, showToast: false }));
    }, 3000);
  };

  // Homepage Product Sections Management
  const handleCreateSection = () => {
    const empty = { ...createEmptyHomepageProductSection(), order: state.homepageSections.length };
    setState(prev => ({
      ...prev,
      editingSection: { ...empty, backgroundColor: 'pink' } as HomepageProductSection
    }));
    setSectionForm({ ...empty, backgroundColor: 'pink' });
  };

  const handleEditSection = (section: HomepageProductSection) => {
    setState(prev => ({ ...prev, editingSection: section }));
    setSectionForm(section);
  };

  const handleSaveSection = async () => {
    try {
      const isExistingSection =
        state.editingSection?.id &&
        state.homepageSections.some((s) => s.id === state.editingSection!.id);

      const payload: Partial<HomepageProductSection> = {
        ...sectionForm,
        backgroundColor: sectionForm.reverse ? 'white' : 'pink'
      };

      if (isExistingSection) {
        await storefrontService.updateHomepageProductSection(state.editingSection!.id, payload);
        showToast('Section updated successfully');
      } else {
        await storefrontService.createHomepageProductSection({
          ...payload,
          order: state.homepageSections.length
        });
        showToast('Section created successfully');
      }

      setState(prev => ({ ...prev, editingSection: null }));
      await loadStorefrontData();
    } catch (error) {
      console.error('Error saving section:', error);
      showToast('Failed to save section', 'error');
    }
  };

  const handleDeleteSection = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await storefrontService.deleteHomepageProductSection(id);
        showToast('Section deleted successfully');
        await loadStorefrontData();
      } catch (error) {
        console.error('Error deleting section:', error);
        showToast('Failed to delete section', 'error');
      }
    }
  };

  const handleCancelEdit = () => {
    setState(prev => ({ ...prev, editingSection: null }));
    setSectionForm(createEmptyHomepageProductSection());
  };

  const handleReorderClick = () => {
    if (state.isReordering) {
      setState(prev => ({ ...prev, isReordering: false, reorderedSections: null }));
      return;
    }
    setState(prev => ({
      ...prev,
      isReordering: true,
      reorderedSections: [...prev.homepageSections]
    }));
  };

  const handleReorderUpdate = async () => {
    if (!state.reorderedSections || state.reorderedSections.length === 0) {
      setState(prev => ({ ...prev, isReordering: false, reorderedSections: null, draggingSectionIndex: null }));
      return;
    }
    try {
      await storefrontService.reorderHomepageProductSections(state.reorderedSections);
      showToast('Sections reordered successfully');
      setState(prev => ({ ...prev, isReordering: false, reorderedSections: null, draggingSectionIndex: null }));
      await loadStorefrontData();
    } catch (error) {
      console.error('Error reordering sections:', error);
      showToast('Failed to reorder sections', 'error');
    }
  };

  const handleSectionDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setState(prev => ({ ...prev, draggingSectionIndex: index }));
  };

  const handleSectionDragEnd = () => {
    setState(prev => ({ ...prev, draggingSectionIndex: null }));
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndexStr = e.dataTransfer.getData('text/plain');
    if (dragIndexStr === '') return;
    const dragIndex = parseInt(dragIndexStr, 10);
    if (Number.isNaN(dragIndex) || dragIndex === dropIndex) return;
    setState(prev => {
      if (!prev.reorderedSections) return prev;
      const list = [...prev.reorderedSections];
      const [removed] = list.splice(dragIndex, 1);
      list.splice(dropIndex, 0, removed);
      return { ...prev, reorderedSections: list };
    });
  };

  // Hero Section Management
  const handleEditHero = () => {
    setState(prev => ({ ...prev, editingHero: true }));
  };

  const handleSaveHero = async () => {
    try {
      await storefrontService.updateHeroSectionData(heroForm);
      showToast('Hero section updated successfully');
      setState(prev => ({ ...prev, editingHero: false }));
      await loadStorefrontData();
    } catch (error) {
      console.error('Error saving hero section:', error);
      showToast('Failed to save hero section', 'error');
    }
  };

  const handleCancelHeroEdit = () => {
    setState(prev => ({ ...prev, editingHero: false }));
    if (state.heroSection) {
      setHeroForm(state.heroSection);
    }
  };

  // Promotional Banner Management
  const handleEditBanner = () => {
    setState(prev => ({ ...prev, editingBanner: true }));
  };

  const handleSaveBanner = async () => {
    try {
      await storefrontService.updatePromotionalBannerData(bannerForm);
      showToast('Promotional banner updated successfully');
      setState(prev => ({ ...prev, editingBanner: false }));
      await loadStorefrontData();
    } catch (error) {
      console.error('Error saving promotional banner:', error);
      showToast('Failed to save promotional banner', 'error');
    }
  };

  const handleCancelBannerEdit = () => {
    setState(prev => ({ ...prev, editingBanner: false }));
    if (state.promotionalBanner) {
      setBannerForm({
        ...state.promotionalBanner,
        note: state.promotionalBanner.note || '' // Ensure note field exists
      });
    }
  };

  const renderHomepageSections = () => {
    const sectionsToShow = state.isReordering && state.reorderedSections
      ? state.reorderedSections
      : state.homepageSections;

    return (
    <div className="storefront-section">
      <div className="section-header">
        <h3>Homepage Product Sections</h3>
        <div className="section-header-actions">
          {state.isReordering ? (
            <Button onClick={handleReorderUpdate} variant="primary">
              Update
            </Button>
          ) : (
            <Button onClick={handleReorderClick} variant="secondary" size="small">
              Reorder
            </Button>
          )}
          <Button
            onClick={handleCreateSection}
            variant="primary"
            disabled={state.isReordering}
          >
            <Icon type="add" size="small" />
            Add Section
          </Button>
        </div>
      </div>

      {state.editingSection && (
        <div className="edit-form">
          <h4>{state.editingSection.id ? 'Edit Section' : 'Create Section'}</h4>
          <div className="form-grid">
            <Input
              label="Title"
              value={sectionForm.title || ''}
              onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />
            <Input
              label="Button Text"
              value={sectionForm.buttonText || ''}
              onChange={(e) => setSectionForm(prev => ({ ...prev, buttonText: e.target.value }))}
              required
            />
            <Input
              label="Button Link"
              value={sectionForm.buttonLink || ''}
              onChange={(e) => setSectionForm(prev => ({ ...prev, buttonLink: e.target.value }))}
              required
            />
            <Input
              type="select"
              label="Link to collection"
              value={sectionForm.collectionId || ''}
              onChange={(e) => setSectionForm(prev => ({ ...prev, collectionId: e.target.value || undefined }))}
              options={[
                { value: '', label: 'None' },
                ...state.collections.map((c) => ({ value: c.id, label: c.name }))
              ]}
            />
            <div className="form-field full-width">
              <HomepageImageUploader
                sectionId={state.editingSection?.id}
                currentImageSrc={sectionForm.imageSrc}
                onImageSelect={(imageUrl) => setSectionForm(prev => ({ ...prev, imageSrc: imageUrl }))}
              />
            </div>
            <Input
              label="Image Alt Text"
              value={sectionForm.imageAlt || ''}
              onChange={(e) => setSectionForm(prev => ({ ...prev, imageAlt: e.target.value }))}
              required
            />
            <div className="form-field full-width">
              <label>Description</label>
              <textarea
                value={sectionForm.description || ''}
                onChange={(e) => setSectionForm(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                required
              />
            </div>
            <div className="form-field">
              <label>
                <input
                  type="checkbox"
                  checked={sectionForm.reverse || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSectionForm(prev => ({
                      ...prev,
                      reverse: checked,
                      backgroundColor: checked ? 'white' : 'pink'
                    }));
                  }}
                />
                Reverse Layout
              </label>
            </div>
            <p className="form-field-hint">When checked, background is white; when unchecked, background is pink.</p>
            <div className="form-field">
              <label>
                <input
                  type="checkbox"
                  checked={sectionForm.isActive !== false}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
          </div>
          <div className="form-actions">
            <Button onClick={handleSaveSection} variant="primary">Save</Button>
            <Button onClick={handleCancelEdit} variant="secondary">Cancel</Button>
          </div>
        </div>
      )}

      <div className={`sections-list ${state.isReordering ? 'sections-list--reorder' : ''}`}>
        {sectionsToShow.map((section, index) => (
          <div
            key={section.id}
            className={`section-card ${state.isReordering ? 'section-card--draggable' : ''} ${state.draggingSectionIndex === index ? 'section-card--dragging' : ''}`}
            draggable={state.isReordering}
            onDragStart={state.isReordering ? (e) => handleSectionDragStart(e, index) : undefined}
            onDragEnd={state.isReordering ? handleSectionDragEnd : undefined}
            onDragOver={state.isReordering ? handleSectionDragOver : undefined}
            onDrop={state.isReordering ? (e) => handleSectionDrop(e, index) : undefined}
          >
            <div className="section-preview">
              {state.isReordering && (
                <span className="section-drag-handle" aria-hidden>
                  ⋮⋮
                </span>
              )}
              <img src={section.imageSrc} alt={section.imageAlt} />
              <div className="section-info">
                <h4>{section.title}</h4>
                <div className="section-meta">
                  <span className={`badge ${section.backgroundColor}`}>{section.backgroundColor}</span>
                  <span className={`badge ${section.reverse ? 'reverse' : 'normal'}`}>
                    {section.reverse ? 'Reverse' : 'Normal'}
                  </span>
                  <span className={`badge ${section.isActive ? 'active' : 'inactive'}`}>
                    {section.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            {!state.isReordering && (
              <div className="section-actions">
                <Button onClick={() => handleEditSection(section)} variant="secondary" size="small">
                  <Icon type="edit" size="small" />
                  Edit
                </Button>
                <Button onClick={() => handleDeleteSection(section.id)} variant="secondary" size="small">
                  <Icon type="delete" size="small" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
  };

  const renderHeroSection = () => (
    <div className="storefront-section">
      <div className="section-header">
        <h3>Hero Section</h3>
        {!state.editingHero && (
          <Button onClick={handleEditHero} variant="primary">
            <Icon type="edit" size="small" />
            Edit
          </Button>
        )}
      </div>

      {state.editingHero ? (
        <div className="edit-form">
          <div className="form-grid">
            <Input
              label="Title"
              value={heroForm.title || ''}
              onChange={(e) => setHeroForm(prev => ({ ...prev, title: e.target.value }))}
            />
            <Input
              label="Subtitle"
              value={heroForm.subtitle || ''}
              onChange={(e) => setHeroForm(prev => ({ ...prev, subtitle: e.target.value }))}
            />
            <Input
              label="Button Text"
              value={heroForm.buttonText || ''}
              onChange={(e) => setHeroForm(prev => ({ ...prev, buttonText: e.target.value }))}
            />
            <Input
              label="Button Link"
              value={heroForm.buttonLink || ''}
              onChange={(e) => setHeroForm(prev => ({ ...prev, buttonLink: e.target.value }))}
            />
            <div className="form-field full-width">
              <HomepageImageUploader
                sectionId="hero-section"
                currentImageSrc={heroForm.imageSrc}
                onImageSelect={(imageUrl) => setHeroForm(prev => ({ ...prev, imageSrc: imageUrl }))}
              />
            </div>
            <Input
              label="Image Alt Text"
              value={heroForm.imageAlt || ''}
              onChange={(e) => setHeroForm(prev => ({ ...prev, imageAlt: e.target.value }))}
            />
            <div className="form-field">
              <label>
                <input
                  type="checkbox"
                  checked={heroForm.isActive !== false}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
          </div>
          <div className="form-actions">
            <Button onClick={handleSaveHero} variant="primary">Save</Button>
            <Button onClick={handleCancelHeroEdit} variant="secondary">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="hero-preview">
          {state.heroSection && (
            <>
              <img src={state.heroSection.imageSrc} alt={state.heroSection.imageAlt} />
              <div className="hero-info">
                <h4>{state.heroSection.title || 'No title set'}</h4>
                <p>{state.heroSection.subtitle || 'No subtitle set'}</p>
                <div className="hero-meta">
                  <span className={`badge ${state.heroSection.isActive ? 'active' : 'inactive'}`}>
                    {state.heroSection.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderPromotionalBanner = () => (
    <div className="storefront-section">
      <div className="section-header">
        <h3>Promotional Banner</h3>
        {!state.editingBanner && (
          <Button onClick={handleEditBanner} variant="primary">
            <Icon type="edit" size="small" />
            Edit
          </Button>
        )}
      </div>

      {state.editingBanner ? (
        <div className="edit-form">
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Message</label>
              <textarea
                value={bannerForm.message || ''}
                onChange={(e) => setBannerForm(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
                required
              />
            </div>
            <Input
              label="Promo Code"
              value={bannerForm.code || ''}
              onChange={(e) => setBannerForm(prev => ({ ...prev, code: e.target.value }))}
              required
            />
            <Input
              label="Dates (Optional)"
              value={bannerForm.dates || ''}
              onChange={(e) => setBannerForm(prev => ({ ...prev, dates: e.target.value }))}
            />
            <div className="form-field full-width">
              <label>Note (Optional)</label>
              <textarea
                value={bannerForm.note || ''}
                onChange={(e) => setBannerForm(prev => ({ ...prev, note: e.target.value }))}
                rows={2}
                placeholder="Additional note or information"
              />
            </div>
            <div className="form-field">
              <label>
                <input
                  type="checkbox"
                  checked={bannerForm.isActive !== false}
                  onChange={(e) => setBannerForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
          </div>
          <div className="form-actions">
            <Button onClick={handleSaveBanner} variant="primary">Save</Button>
            <Button onClick={handleCancelBannerEdit} variant="secondary">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="banner-preview">
          {state.promotionalBanner && (
            <div className="promotional-banner-preview">
              <div className="banner-content">
                <div className="banner-main">
                  <span className="banner-text">
                    {state.promotionalBanner.message} - use code <strong className="banner-code">{state.promotionalBanner.code}</strong> at checkout!
                  </span>
                  
                  {state.promotionalBanner.note && (
                    <span className="banner-note">
                      {state.promotionalBanner.note}
                    </span>
                  )}
                </div>
                <button className="banner-arrow">
                  <Icon type="arrow" size="small" />
                </button>
              </div>
              <div className="banner-meta">
                <span className={`badge ${state.promotionalBanner.isActive ? 'active' : 'inactive'}`}>
                  {state.promotionalBanner.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (state.loading) {
    return (
      <div className="storefront-page">
        <div className="loading-state">
          <h2>Loading Storefront Data...</h2>
          <p>Please wait while we fetch your storefront content.</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="storefront-page">
        <div className="error-state">
          <h2>Error Loading Storefront Data</h2>
          <p>{state.error}</p>
          <Button onClick={loadStorefrontData} variant="primary">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="storefront-page">
      <div className="page-header">
        <h1>Storefront Management</h1>
        <p>Manage your homepage content including product sections, hero section, and promotional banner.</p>
        {state.homepageSections.length === 0 && !state.heroSection && !state.promotionalBanner && (
          <div className="init-data-section">
            <p>No storefront data found. Initialize with default content:</p>
            <Button onClick={handleInitializeData} variant="primary">
              Initialize Default Data
            </Button>
          </div>
        )}
      </div>

      <div className="tabs">
        <button
          className={`tab ${state.activeTab === 'sections' ? 'active' : ''}`}
          onClick={() => setState(prev => ({ ...prev, activeTab: 'sections' }))}
        >
          Product Sections
        </button>
        <button
          className={`tab ${state.activeTab === 'hero' ? 'active' : ''}`}
          onClick={() => setState(prev => ({ ...prev, activeTab: 'hero' }))}
        >
          Hero Section
        </button>
        <button
          className={`tab ${state.activeTab === 'banner' ? 'active' : ''}`}
          onClick={() => setState(prev => ({ ...prev, activeTab: 'banner' }))}
        >
          Promotional Banner
        </button>
      </div>

      <div className="tab-content">
        {state.activeTab === 'sections' && renderHomepageSections()}
        {state.activeTab === 'hero' && renderHeroSection()}
        {state.activeTab === 'banner' && renderPromotionalBanner()}
      </div>

      {state.showToast && (
        <Toast
          id="storefront-toast"
          title={state.toastType === 'success' ? 'Success' : 'Error'}
          message={state.toastMessage}
          type={state.toastType}
          onClose={() => setState(prev => ({ ...prev, showToast: false }))}
        />
      )}
    </div>
  );
};

export default StorefrontPage;
